import { useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { LocationPush } from '../services/api';

export function useTracking(jobId: string | null) {
    const [location, setLocation] = useState<LocationPush | null>(null);
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const hubRef = useRef<signalR.HubConnection | null>(null);

    useEffect(() => {
        if (!jobId) return;
        const token = localStorage.getItem('br_token');
        const hub = new signalR.HubConnectionBuilder()
            .withUrl(`${process.env.REACT_APP_API_URL || 'http://10.0.0.175:5000'}/hubs/tracking?access_token=${token}`, { withCredentials: false })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

        hub.on('ContractorLocationUpdated', (push: LocationPush) => {
            if (push.jobId === jobId) setLocation(push);
        });

        // ← Add this — receives price adjustment and status change events
        hub.on('JobStatusUpdated', (push: { jobStatus: string; jobId: string }) => {
            if (push.jobId === jobId) {
                setLocation(prev => ({
                    ...prev,
                    jobId: push.jobId,
                    jobStatus: push.jobStatus,
                    lat: prev?.lat ?? 0,
                    lng: prev?.lng ?? 0,
                    timestamp: new Date().toISOString()
                } as LocationPush));
            }
        });

        // hub.start() can hang without ever resolving or rejecting if the WebSocket handshake
        // gets stuck (seen in practice — negotiate succeeds but the follow-up connection to the
        // Azure SignalR Service endpoint never completes), which would otherwise leave both
        // `connected` and `error` at their initial falsy values forever with no feedback at all.
        let settled = false;
        const timeout = setTimeout(() => {
            if (!settled) { settled = true; setError('Connection timed out.'); }
        }, 15000);

        hub.start()
            .then(() => {
                if (settled) return; // timed out before it finally connected — let onclose/manual retry handle it
                settled = true; clearTimeout(timeout);
                setError(null); hub.invoke('SubscribeToJob', jobId); setConnected(true);
            })
            .catch(e => {
                if (settled) return;
                settled = true; clearTimeout(timeout);
                console.error(e); setError(e.message || 'Could not connect for live updates.');
            });

        hub.onclose(() => setConnected(false));
        hubRef.current = hub;
        return () => { clearTimeout(timeout); hub.stop(); };
    }, [jobId]);

    return { location, connected, error };
}